import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { alpha, Box, Dialog, IconButton, Typography } from '@mui/material';
import { X } from 'lucide-react';


const Assistant = lazy(() => import('../pages/Assistant'));

function visibleViewport() {
  return {
    height: window.visualViewport?.height || window.innerHeight,
    top: window.visualViewport?.offsetTop || 0,
  };
}

export default function AssistantOverlay({ open, onClose, initialConversationId = null }) {
  const [viewport, setViewport] = useState(visibleViewport);
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    if (!open) return undefined;
    const update = () => setViewport(visibleViewport());
    const visualViewport = window.visualViewport;
    update();
    window.addEventListener('resize', update);
    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
    };
  }, [open]);

  return <Dialog
    id="assistant-overlay"
    aria-label="Dopatize assistant"
    open={open}
    onClose={onClose}
    keepMounted
    disableRestoreFocus
    maxWidth={false}
    style={{ top: viewport.top, height: viewport.height, bottom: 'auto' }}
    slotProps={{
      transition: { onExited: () => {
        if (!openRef.current) document.querySelector('#app-assistant-launcher, #app-leaderboard-launcher')?.focus({ preventScroll: true });
      } },
      paper: { 'aria-label': 'Dopatize assistant', sx: theme => ({
        width: { xs: '100%', sm: 700 }, height: { xs: '100%', sm: 'min(760px, 100%)' },
        maxWidth: '100%', maxHeight: '100%', minHeight: 0, m: 0, overflow: 'hidden', position: 'relative',
        borderRadius: { xs: '22px', sm: '28px' },
        border: `1px solid ${alpha(theme.palette.primary.main, .4)}`,
        bgcolor: theme.palette.background.default,
        boxShadow: `0 12px 60px ${alpha(theme.palette.common.black, .65)}, 0 0 24px ${alpha(theme.palette.primary.main, .12)}`,
      }) },
    }}
    sx={{ '& .MuiDialog-container': {
      alignItems: 'flex-end', justifyContent: 'flex-start',
      p: { xs: 1, sm: 2 }, pb: 'max(8px, env(safe-area-inset-bottom))',
    } }}
  >
    <IconButton aria-label="Close assistant" onClick={onClose} sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1, color: 'primary.main', bgcolor: 'background.default' }}><X size={20} /></IconButton>
    <Box onClickCapture={event => {
      if (event.target.closest('a')?.getAttribute('href')?.startsWith('/')) onClose();
    }} sx={{ p: { xs: 1.5, sm: 2 }, height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Suspense fallback={<Typography role="status" sx={{ mt: 5, fontSize: '.65rem' }}>Opening assistant…</Typography>}>
        <Assistant embedded active={open} initialConversationId={initialConversationId} viewportHeight={viewport.height} />
      </Suspense>
    </Box>
  </Dialog>;
}
